<script setup lang="ts">
import { createErrorMessage, defaultSolution } from '@errors/helpers';
import { useFeatureToggle } from '@environment/useFeatureToggle';
import { useMuterenGetMogelijkeMutatieActies } from '@services/aanvragen-public/orval-api/muteren-api/muteren-api';
import apiService from '@services/base/apiService';
import { type AanvragenPolissenGetPakketPolissenByRelatiePakketPolisDto as PakketPolisDto } from '@services/polis-public/orval-api/model/aanvragenPolissenGetPakketPolissenByRelatiePakketPolisDto';
import { VButton } from '@shared/component/VButton';
import VCollapsibleContentBox from '@shared/component/VCollapsibleContentBox/VCollapsibleContentBox.vue';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import VMenu from '@shared/component/VMenu/VMenu.vue';
import VModal from '@shared/component/VModal/VModal.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import VStatusTag, { type VStatusTagProps } from '@shared/component/VStatusTag/VStatusTag.vue';
import VTooltip from '@shared/component/VTooltip/VTooltip.vue';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import { useMutation } from '@tanstack/vue-query';
import { computed, type ComputedRef, ref, toRefs, watch } from 'vue';
import { useRelatiesGetRelationByRelatienummer } from '@services/crm-public/orval-api/relaties-api/relaties-api';
import { hasPermission } from '@permission/hasPermission';
import PakketpolisBeursMutatieModal from '@crm-lib/Polissen/modals/PakketpolisBeursMutatieModal.vue';
import PakketpolisDummyMutatieStartenModal from './modals/PakketpolisDummyMutatieStartenModal.vue';
import PakketpolisRoyerenModal from './modals/PakketpolisRoyerenModal.vue';
import PakketpolisDetails from './PakketpolisDetails.vue';
import PakketpolisMuterenModal from './modals/PakketpolisMuterenModal.vue';
import SubpolisToevoegenModal from './modals/SubpolisToevoegenModal.vue';
import PakketpolisToolbar from './PakketpolisToolbar.vue';
import SubpolisList from './SubpolisList.vue';
import WagenparkcontractAfspraken from './wagenparkcontract/WagenparkcontractAfspraken.vue';
import { usePakketpolisStore } from './pakketpolisStore';

export type MutatieGestart = {
  directTeVerwerken: boolean;
  offertenummer: string;
  offerteproductId: number | null | undefined;
  isBeursPakket: boolean;
  wagenparkId?: string | null;
};

const props = defineProps<{
  polis: PakketPolisDto;
  relatienummer: number;
}>();

const emit = defineEmits<{
  mutatieGestart: [MutatieGestart];
}>();

const open = defineModel<boolean>('open', { required: true });

const { polis, relatienummer } = toRefs(props);

const {
  isPending: isGetMogelijkeMutatieActiesPending,
  isError,
  error,
  data: mutatieActies,
} = useMuterenGetMogelijkeMutatieActies(
  polis.value.polisnummer,
  { relatienummer: relatienummer.value, contractnummer: polis.value.contractnummer ?? undefined },
  {
    query: {
      select: (result) => result.result,
      retry: 3,
    },
  },
);

const { data: relatiegegevens } = useRelatiesGetRelationByRelatienummer(relatienummer, {
  query: { select: (q) => q.result },
});

const store = usePakketpolisStore();

const pakketpolis = computed(() => {
  if (!mutatieActies.value) {
    return undefined;
  }

  store.addPakketpolis(polis.value, mutatieActies.value);

  return store.getPakketpolis(polis.value.polisnummer);
});

const notification = useNotification();
const wagenparkMutatieToggle = useFeatureToggle('WagenparkMutatieToggle');

const isPakketPolisMuterenModalOpen = ref<boolean>(false);
const isPakketBeursPolisMuterenModalOpen = ref<boolean>(false);
const isPakketPolisDummyMutatieModalOpen = ref<boolean>(false);
const isPakketPolisRoyerenModalOpen = ref<boolean>(false);
const confirmDuplicerenOpen = ref<boolean>(false);
const isPolisToevoegenModalOpen = ref<boolean>(false);

const magMutatieStarten = hasPermission('Muteren.MutatieStarten');
const magRoyeren = hasPermission('Muteren.Royeren');
const isWagenpark = computed(() => polis.value.isWagenpark);
const aantalMutaties = computed(() => pakketpolis.value?.aantalMutaties ?? 0);
const toonToolbar = computed(
  () =>
    !isWagenpark.value &&
    pakketpolis.value?.mutatieActies.muteren.isMogelijk &&
    (magRoyeren.value || magMutatieStarten.value) &&
    aantalMutaties.value > 0,
);

watch(
  () => toonToolbar.value,
  (toon) => {
    if (toon && !open.value) {
      open.value = true;
    }
  },
);

type MenuItem = {
  label: string;
  disabled: boolean;
  reden?: string;
  icon: string;
  command: () => void;
};
type MenuItemGroup = { label: string; items: MenuItem[] };
const menu = ref<InstanceType<typeof VMenu>>();
const menuOptions: ComputedRef<Array<MenuItem | MenuItemGroup>> = computed(() => {
  if (!pakketpolis.value) {
    return [];
  }

  if (isWagenpark.value) {
    const actie = pakketpolis.value.mutatieActies.dummymutatieStarten;
    return [
      {
        label: 'Muteren',
        disabled: !wagenparkMutatieToggle.value || !actie?.isMogelijk,
        reden: actie?.melding ?? 'Muteren via SUREnet binnenkort beschikbaar.',
        command: () => (isPakketPolisDummyMutatieModalOpen.value = true),
        icon: 'far fa-pen',
      },
    ];
  }

  const hasInternalOptions = !!pakketpolis.value.mutatieActies.dummymutatieStarten;
  const isBeursPolis = computed(() => polis.value.polisnummer.substring(0, 3) === 'SBB');

  return [
    {
      label: 'Muteren',
      disabled: !pakketpolis.value.mutatieActies.muteren.isMogelijk,
      reden: pakketpolis.value.mutatieActies.muteren.melding ?? undefined,
      command: () => {
        if (isBeursPolis.value) {
          openBeursMuterenModal();
        } else {
          openMuterenModal();
        }
      },
      icon: 'far fa-pen',
    },
    {
      label: 'Royeer pakket',
      command: openRoyerenModal,
      disabled: !pakketpolis.value.mutatieActies.royeren.isMogelijk,
      reden: pakketpolis.value.mutatieActies.royeren.melding ?? undefined,
      icon: 'far fa-times',
    },
    {
      label: 'Product toevoegen',
      disabled: !pakketpolis.value.mutatieActies.productToevoegen.isMogelijk,
      command: () => (isPolisToevoegenModalOpen.value = true),
      reden: pakketpolis.value.mutatieActies.productToevoegen.melding ?? undefined,
      icon: 'far fa-plus',
    },
    {
      label: 'Kopie polisblad',
      disabled: !pakketpolis.value.mutatieActies.dupliceren.isMogelijk,
      command: () => (confirmDuplicerenOpen.value = true),
      reden: pakketpolis.value.mutatieActies.dupliceren.melding ?? undefined,
      icon: 'far fa-files',
    },
    ...(hasInternalOptions
      ? [
          {
            label: 'Interne opties',
            items: [
              {
                label: 'Dummy mutatie',
                disabled: !pakketpolis.value.mutatieActies.dummymutatieStarten?.isMogelijk,
                command: () => (isPakketPolisDummyMutatieModalOpen.value = true),
                reden: pakketpolis.value.mutatieActies.dummymutatieStarten?.melding ?? undefined,
                icon: 'far fa-file-pen',
              },
            ],
          },
        ]
      : []),
  ];
});

const polissen = computed(() => polis.value.subpolissen.filter((p) => !p.isGeroyeerd));
const geroyeerdePolissen = computed(() => polis.value.subpolissen.filter((p) => p.isGeroyeerd));
const actievePolissenOpen = ref(polissen.value.length > 0);
const geroyeerdePolissenOpen = ref(
  geroyeerdePolissen.value.length > 0 && polissen.value.length === 0,
);

const tag = computed<{ text: string; statusColor: VStatusTagProps['color'] }>(() => {
  if (polis.value.isGeroyeerd || polis.value.isGeschorst) {
    return {
      text: polis.value.isGeroyeerd ? 'Geroyeerd' : 'Geschorst',
      statusColor: 'red',
    };
  }

  return {
    text: polissen.value.length === 1 ? '1 polis' : `${polissen.value.length} polissen`,
    statusColor: 'grey',
  };
});

const titel = computed(() => {
  if (!isWagenpark.value) {
    return `${polis.value.omschrijving} / ${polis.value.polisnummer}`;
  }

  const contractnummer = polis.value.contractnummer && `SBW${polis.value.contractnummer}`;
  return [polis.value.omschrijving, contractnummer, relatiegegevens.value?.name]
    .filter(Boolean)
    .join(' / ');
});

function toggleMenu(event: MouseEvent) {
  menu.value?.toggle(event);
}

function openMuterenModal() {
  if (!pakketpolis.value?.mutatieActies?.muteren) {
    return;
  }

  isPakketPolisMuterenModalOpen.value = true;
}

function openBeursMuterenModal() {
  if (!pakketpolis.value?.mutatieActies?.muteren) {
    return;
  }

  isPakketBeursPolisMuterenModalOpen.value = true;
}

function openRoyerenModal() {
  isPakketPolisRoyerenModalOpen.value = true;
}

const soortVerzekeringen = computed(() =>
  pakketpolis.value
    ? Array.from(
        new Set(
          pakketpolis.value.originelePakketpolis.subpolissen.map((sp) => sp.soortVerzekering),
        ),
      )
    : undefined,
);

const { isPending, mutate: dupliceerPolis } = useMutation({
  mutationFn: () =>
    apiService.apiClient.post(
      '/net/relation/KopiePolis',
      {
        relatienr: relatienummer.value,
        internpolisnr: polis.value.internPolisnummer,
      },
      { timeout: 5 * 60 * 1000 },
    ),
  onSuccess: (res) => {
    confirmDuplicerenOpen.value = false;
    if (res.data === 'OK') {
      notification.success('De polis is succesvol gekopieerd.', true);
    } else {
      notification.error(`Fout bij aanmaken kopie polis: ${res.data}`);
    }
  },
  onError: () =>
    notification.error(`Er is iets fout gegaan bij het kopiëren van de polis. ${defaultSolution}`),
});

function closeDuplicerenModal() {
  if (isPending.value) return;
  confirmDuplicerenOpen.value = false;
}

const huidigeProducten = computed(() => {
  const count: Record<string, number> = {};
  polis.value.subpolissen
    .filter((sp) => sp.productId)
    .forEach((sp) => {
      count[sp.productId!] = (count[sp.productId!] || 0) + 1;
    });
  return count;
});
</script>

<template>
  <VInfoBox badge-text="error" type="danger" v-if="isError">
    <div v-sanitized-html="createErrorMessage(error)"></div>
  </VInfoBox>
  <div v-else>
    <VCollapsibleContentBox v-model:open="open">
      <template #header-end>
        <VTooltip label="Opties" v-if="magRoyeren || magMutatieStarten">
          <button
            type="button"
            aria-haspopup="menu"
            aria-controls="overlay_menu"
            aria-label="menu"
            @click="toggleMenu"
            class="flex size-10 cursor-pointer items-center justify-center rounded border-none bg-grey-100 hover:bg-grey-200 focus:outline-none"
            :class="{ 'bg-grey-200': menu?.overlayVisible() }"
          >
            <FontAwesomeIcon icon="fas fa-ellipsis-vertical" class="text-primary-900" />
          </button>
        </VTooltip>
        <VMenu popup :pt="{ root: { id: 'overlay_menu' } }" ref="menu" :model="menuOptions">
          <template #start>
            <VSkeleton v-if="isGetMogelijkeMutatieActiesPending" height="lg" :count="4" />
          </template>
          <template #item="{ item }">
            <VTooltip :label="item.reden" :active="item.disabled && !!item.reden">
              <div
                class="flex items-center overflow-hidden p-2"
                :class="{ 'cursor-not-allowed text-grey': item.disabled }"
              >
                <FontAwesomeIcon
                  class="mr-2 shrink-0 [--fa-fw-width:16px]"
                  fixed-width
                  v-if="item.icon"
                  :icon="item.icon"
                />
                <div>{{ item.label }}</div>
              </div>
            </VTooltip>
          </template>
        </VMenu>
      </template>
      <template #header>
        <button
          type="button"
          class="flex w-full cursor-pointer flex-wrap gap-x-4 text-inherit"
          @click="open = !open"
        >
          {{ titel }}
          <VStatusTag
            v-if="(!open || polis.isGeschorst || polis.isGeroyeerd) && tag"
            :color="tag.statusColor"
          >
            {{ tag.text }}
          </VStatusTag>
        </button>
      </template>
      <div
        class="relative divide-x-0 divide-y divide-solid divide-grey-200 [&>.collapsible-section]:border-t-0"
      >
        <VSkeleton v-if="isGetMogelijkeMutatieActiesPending" height="lg" :count="3" class="p-6" />
        <PakketpolisDetails
          v-else
          :pakketpolisnummer="polis.polisnummer"
          @edit="openMuterenModal"
        />
        <WagenparkcontractAfspraken
          v-if="polis.contractnummer && relatienummer"
          :contractnummer="polis.contractnummer"
          :relatienummer="relatienummer"
        />
        <SubpolisList
          v-if="polissen.length && relatiegegevens"
          title="Polissen"
          show-nieuwe-producten
          :adviseurnummer="relatiegegevens?.advisorId"
          :pakket-id="pakketpolis?.originelePakketpolis.pakketId"
          :polissen="polissen"
          :pakketpolisnummer="polis.polisnummer"
          :hide-options="isWagenpark"
          v-model:open="actievePolissenOpen"
        >
          <template #footer v-if="!isWagenpark">
            <VTooltip
              v-if="magRoyeren || magMutatieStarten"
              class="w-full"
              :active="!!pakketpolis?.mutatieActies.productToevoegen.melding"
              :label="pakketpolis?.mutatieActies.productToevoegen.melding!"
            >
              <VButton
                v-if="!polis.isGeschorst"
                type="primary"
                :inverted="true"
                prepend-icon="plus"
                font-size="sm"
                :hide-shadow="true"
                @click="isPolisToevoegenModalOpen = true"
                :disabled="!pakketpolis?.mutatieActies.productToevoegen.isMogelijk"
                class="mt-1 h-14 w-full justify-center border-grey-200"
              >
                Product toevoegen
              </VButton>
            </VTooltip>
          </template>
        </SubpolisList>

        <SubpolisList
          v-if="geroyeerdePolissen.length && relatiegegevens"
          title="Geroyeerde Polissen"
          :adviseurnummer="relatiegegevens.advisorId"
          :polissen="geroyeerdePolissen"
          :pakketpolisnummer="polis.polisnummer"
          v-model:open="geroyeerdePolissenOpen"
          :show-nieuwe-producten="false"
        />

        <div v-if="toonToolbar" class="sticky bottom-0 bg-white p-4 lg:p-6 lg:pr-8">
          <PakketpolisToolbar
            :pakketpolisnummer="polis.polisnummer"
            @mutatie-gestart="(v) => emit('mutatieGestart', v)"
          />
        </div>
      </div>
    </VCollapsibleContentBox>
    <PakketpolisMuterenModal
      :pakketpolisnummer="polis.polisnummer"
      v-model:is-open="isPakketPolisMuterenModalOpen"
    />
    <PakketpolisBeursMutatieModal
      :pakketpolisnummer="polis.polisnummer"
      v-model:is-open="isPakketBeursPolisMuterenModalOpen"
      @mutatie-gestart="(v) => emit('mutatieGestart', v)"
    />
    <PakketpolisDummyMutatieStartenModal
      :pakketpolisnummer="polis.polisnummer"
      :relatienummer="relatienummer"
      :titel="isWagenpark ? 'Mutatie starten' : undefined"
      :is-wagenpark="isWagenpark"
      v-model:is-open="isPakketPolisDummyMutatieModalOpen"
      @mutatie-gestart="(v) => emit('mutatieGestart', v)"
    />
    <PakketpolisRoyerenModal
      v-if="pakketpolis"
      :polisnummer="polis.polisnummer"
      :soort-verzekeringen="soortVerzekeringen"
      :royeer-informatie="mutatieActies?.royeren!"
      v-model:is-open="isPakketPolisRoyerenModalOpen"
    />
    <VModal
      :loading="isPending"
      @confirm-action="dupliceerPolis()"
      @close-modal="closeDuplicerenModal()"
      :is-active="confirmDuplicerenOpen"
      confirm-button-text="Kopie polisblad aanmaken"
    >
      Weet je zeker dat je een kopie van het polisblad wilt aanmaken? Dit kan een paar minuten in
      beslag nemen.
    </VModal>
    <SubpolisToevoegenModal
      v-if="relatiegegevens"
      :adviseurnummer="relatiegegevens?.advisorId"
      :pakketpolisnummer="polis.polisnummer"
      :huidige-producten="huidigeProducten"
      :pakket-id="pakketpolis?.originelePakketpolis.pakketId"
      v-model:is-open="isPolisToevoegenModalOpen"
    />
  </div>
</template>
